import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
gsap.registerPlugin(SplitText);

import { Basics } from '../_app/cuchillo/core/Basics';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Ease } from '../_app/cuchillo/utils/Ease';

const AUDIO_SRC = '/assets/audio/ambient-loop-ok.wav';
const VOLUME = 0.035;

export default class AudioController {
  static isPlaying = false;
  static _ctx = null;
  static _buffer = null;
  static _source = null;
  static _gainNode = null;
  static _loadPromise = null;
  static _startTime = 0;
  static _rafId = null;
  static _btn = null;
  static _layer = null;
  static _content = null;
  static _timeEl = null;
  static _torcido = null;
  static _torcidoSpan = null;
  static _torcidoW = 0;
  static _torcidoH = 0;
  static _text;

  static init() {
    this._btn = GetBy.id('Volume');
    if (this._btn) {
      this._btn.addEventListener(Basics.clickEvent, () => this.toggle());
    }
    this._timeEl = GetBy.id('AudioTime');

    this._layer = GetBy.id('AudioLayer');
    // Wrapper intern (title + text + button). Arrenca amagat via CSS (opacity:0)
    // per evitar el glitch de setup; el fade-in es fa a showLayer().
    this._content = this._layer ? this._layer.children[0] : null;
    // if (this._layer) {
    // gsap.set(this._layer, { opacity: 0, pointerEvents: 'none' });

    this._torcido = GetBy.selector('.button-torcido', this._layer)[0];
    this._torcidoSpan = this._torcido ? GetBy.selector('span', this._torcido)[0] : null;
    if (this._torcido) {
      this._torcidoW = this._torcido.offsetWidth;
      this._torcidoH = this._torcido.offsetHeight;
      gsap.set(this._torcido, { width: 0, height: this._torcidoH });
      if (this._torcidoSpan) gsap.set(this._torcidoSpan, { opacity: 0 });
    }

    this.timeline = gsap.timeline({ paused: true });

    this._text = new SplitText(GetBy.class('text', this._layer)[0], {
      type: 'words',
      wordsClass: 'line-child'
    }).words;

    const tempHeight = this._text[0].offsetHeight;
    gsap.set(this._text, { y: tempHeight * 3, rotate: 20 });
  }

  // Lazy-init AudioContext + decode buffer. Safe to call multiple times.
  static _load() {
    if (this._loadPromise) return this._loadPromise;

    this._ctx = new (window.AudioContext || window.webkitAudioContext)();
    this._gainNode = this._ctx.createGain();
    this._gainNode.gain.value = VOLUME;
    this._gainNode.connect(this._ctx.destination);

    this._loadPromise = fetch(AUDIO_SRC)
      .then(r => r.arrayBuffer())
      .then(buf => this._ctx.decodeAudioData(buf))
      .then(decoded => { this._buffer = decoded; });

    return this._loadPromise;
  }

  static directHide() {
    gsap.set(this._layer, {
      opacity: 0,
      pointerEvents: 'none'
    });
  }

  static showLayer(onChoice) {
    if (!this._layer) { onChoice(); return; }

    const buttons = GetBy.selector('button', this._layer);
    const btnWith = buttons[0];
    // const btnWithout = buttons[1];

    const dismiss = (withAudio) => {
      btnWith.removeEventListener(Basics.clickEvent, onWith);

      gsap.to(this._layer, {
        opacity: 0,
        duration: 0.6,
        ease: 'power2.out',
        onComplete: () => {
          gsap.set(this._layer, { pointerEvents: 'none' });
          if (withAudio) this.play();
          onChoice();
        }
      });
    };

    const onWith = () => dismiss(true);
    btnWith.addEventListener(Basics.clickEvent, onWith);

    // Fade-in de tot el contingut un cop JS ja ha posicionat els items (evita glitch inicial).
    gsap.to(this._content, { opacity: 1, duration: 0.3, delay: 0.3 });

    gsap.to(this._text, {
      y: 0,
      duration: 1,
      rotate: 0,
      force3D: true,
      ease: Ease.EASE_CUCHILLO_IN_OUT,
      stagger: 0.08
    });

    gsap.to(this._torcido, { width: this._torcidoW, duration: 1.5, delay: 0.5, ease: 'power3.out' });
    gsap.to(this._torcidoSpan, { opacity: 1, duration: 0.1, delay: 0.7 });
  }

  static _fmt(s) {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }

  static _tick() {
    if (!this.isPlaying || !this._timeEl) return;
    const elapsed = (this._ctx.currentTime - this._startTime) % this._buffer.duration;
    this._timeEl.textContent = `${this._fmt(elapsed)} — ${this._fmt(this._buffer.duration)}`;
    this._rafId = requestAnimationFrame(() => this._tick());
  }

  static play() {
    this._load().then(() => {
      if (this._ctx.state === 'suspended') this._ctx.resume();

      this._source = this._ctx.createBufferSource();
      this._source.buffer = this._buffer;
      this._source.loop = true;
      this._source.connect(this._gainNode);
      this._source.start(0);
      this._startTime = this._ctx.currentTime;

      this.isPlaying = true;
      this._btn?.classList.add('--active');
      this._tick();
    }).catch(() => { });
  }

  static stop() {
    if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null; }
    if (this._source) {
      this._source.stop();
      this._source.disconnect();
      this._source = null;
    }
    if (this._timeEl) this._timeEl.textContent = '';
    this.isPlaying = false;
    this._btn?.classList.remove('--active');
  }

  static toggle() {
    this.isPlaying ? this.stop() : this.play();
  }
}
